import Cookies from 'js-cookie';
import React, { useEffect, useState } from 'react';
import useAuth from '../../hook/useAuth';
import useConfirm from '../../hook/useConfirm';
import { getAdminSettings, postAdminSetting, put_AdminSetting } from '../../services/AdminSetting';

const AdminSettings = () => {
    const token = Cookies.get("token-my-cdir");
    const { auth }: any = useAuth();

    const { setConfirmPopup, setData: setConfirmData }: any = useConfirm();

    const [isProcessing, setIsProcessing] = useState(false);
    const [settingId, setSettingId] = useState<number | null>(null);

    const [settings, setSettings] = useState<any>({
        signupNotifyEmail: "",
        signupNotifySubject: "",

        importNotifyEmail: "",
        importNotifySubject: "",

        exportNotifyEmail: "",
        exportNotifySubject: "",

        ticketNotifyEmail: "",
        ticketNotifySubject: "",

        createdAt: "",
        createdBy: "",
        updatedAt: "",
        updatedBy: ""
    });

    useEffect(() => {
        loadSettings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadSettings = async () => {
        try {
            const response = await getAdminSettings(token!.toString());

            const data = Array.isArray(response)
                ? response[0]
                : response;

            if (data) {
                setSettingId(data.id);

                setSettings({
                    signupNotifyEmail: data.signupNotifyEmail || "",
                    signupNotifySubject: data.signupNotifySubject || "",

                    importNotifyEmail: data.importNotifyEmail || "",
                    importNotifySubject: data.importNotifySubject || "",

                    exportNotifyEmail: data.exportNotifyEmail || "",
                    exportNotifySubject: data.exportNotifySubject || "",

                    ticketNotifyEmail: data.ticketNotifyEmail || "",
                    ticketNotifySubject: data.ticketNotifySubject || "",

                    createdAt: data.createdAt || "",
                    createdBy: data.createdBy || "",
                    updatedAt: data.updatedAt || "",
                    updatedBy: data.updatedBy || ""
                });
            }
        } catch (error: any) {
            console.error(error);

            const errorMessage =
                error?.response?.data ||
                error?.message ||
                "Unable to load admin settings.";

            setConfirmData({
                title: 'Warning',
                summary: errorMessage,
                confirmBtn: 'Ok',
                isConfirm: (callback: any) => {
                    callback();
                }
            });

            setConfirmPopup(true);
        }
    }

    const handleChange = (field: string, value: string) => {
        setSettings((prev: any) => ({
            ...prev,
            [field]: value
        }));
    }

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (isProcessing) return;

        setIsProcessing(true);

        try {
            const data = {
                id: settingId,

                signupNotifyEmail: settings.signupNotifyEmail,
                signupNotifySubject: settings.signupNotifySubject,

                importNotifyEmail: settings.importNotifyEmail,
                importNotifySubject: settings.importNotifySubject,

                exportNotifyEmail: settings.exportNotifyEmail,
                exportNotifySubject: settings.exportNotifySubject,

                ticketNotifyEmail: settings.ticketNotifyEmail,
                ticketNotifySubject: settings.ticketNotifySubject,

                createdAt: settings.createdAt,
                createdBy: settings.createdBy,

                updatedAt: new Date().toISOString(),
                updatedBy: auth?.givenname
            };

            if (settingId) {
                await put_AdminSetting(data, token!.toString());
            } else {
                await postAdminSetting(
                    {
                        ...data,
                        createdAt: new Date().toISOString(),
                        createdBy: auth?.givenname
                    },
                    token!.toString()
                );
            }

            setConfirmData({
                title: 'Success',
                summary: 'Admin settings saved successfully.',
                confirmBtn: 'Ok',
                isConfirm: (callback: any) => {
                    callback();
                }
            });

            setConfirmPopup(true);

        } catch (error: any) {
            console.error(error);

            const errorMessage =
                error?.response?.data ||
                error?.message ||
                "Something went wrong, Please try again.";

            setConfirmData({
                title: 'Warning',
                summary: errorMessage,
                confirmBtn: 'Ok',
                isConfirm: (callback: any) => {
                    callback();
                }
            });

            setConfirmPopup(true);
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div className="w-full">
            <div className="bg-gradient-to-r from-cyan-300 to-blue-300 w-full h-60" />

            <div className="-mt-28 mb-6 px-4">
                <div className="mx-auto max-w-6xl shadow-lg py-8 px-6 relative bg-white rounded">

                    <h2 className="text-xl text-secondary font-bold">
                        Admin Settings
                    </h2>

                    <form
                        onSubmit={handleSubmit}
                        className="mt-8 grid xm:grid-cols-2 gap-6 text-secondary"
                    >

                        {/* Signup Notification */}
                        <div className="xm:col-span-2">
                            <h3 className="text-lg font-bold border-b pb-2 mb-4">
                                Signup Notification
                            </h3>
                        </div>

                        <div>
                            <label className="font-semibold text-sm">
                                Notification Email
                            </label>

                            <input
                                type="email"
                                placeholder="Enter signup notification email"
                                className="w-full rounded py-2.5 px-4 mt-2 border-2 text-sm text-black outline-secondary"
                                value={settings.signupNotifyEmail}
                                onChange={(e) =>
                                    handleChange(
                                        "signupNotifyEmail",
                                        e.target.value
                                    )
                                }
                            />
                        </div>

                        <div>
                            <label className="font-semibold text-sm">
                                Email Subject
                            </label>

                            <input
                                type="text"
                                placeholder="Enter signup email subject"
                                className="w-full rounded py-2.5 px-4 mt-2 border-2 text-sm text-black outline-secondary"
                                value={settings.signupNotifySubject}
                                onChange={(e) =>
                                    handleChange(
                                        "signupNotifySubject",
                                        e.target.value
                                    )
                                }
                            />
                        </div>

                        {/* Import Notification */}
                        <div className="xm:col-span-2 mt-4">
                            <h3 className="text-lg font-bold border-b pb-2 mb-4">
                                Import Notification
                            </h3>
                        </div>

                        <div>
                            <label className="font-semibold text-sm">
                                Notification Email
                            </label>

                            <input
                                type="email"
                                placeholder="Enter import notification email"
                                className="w-full rounded py-2.5 px-4 mt-2 border-2 text-sm text-black outline-secondary"
                                value={settings.importNotifyEmail}
                                onChange={(e) =>
                                    handleChange(
                                        "importNotifyEmail",
                                        e.target.value
                                    )
                                }
                            />
                        </div>

                        <div>
                            <label className="font-semibold text-sm">
                                Email Subject
                            </label>

                            <input
                                type="text"
                                placeholder="Enter import email subject"
                                className="w-full rounded py-2.5 px-4 mt-2 border-2 text-sm text-black outline-secondary"
                                value={settings.importNotifySubject}
                                onChange={(e) =>
                                    handleChange(
                                        "importNotifySubject",
                                        e.target.value
                                    )
                                }
                            />
                        </div>

                        {/* Export Notification */}
                        <div className="xm:col-span-2 mt-4">
                            <h3 className="text-lg font-bold border-b pb-2 mb-4">
                                Export Notification
                            </h3>
                        </div>

                        <div>
                            <label className="font-semibold text-sm">
                                Notification Email
                            </label>

                            <input
                                type="email"
                                placeholder="Enter export notification email"
                                className="w-full rounded py-2.5 px-4 mt-2 border-2 text-sm text-black outline-secondary"
                                value={settings.exportNotifyEmail}
                                onChange={(e) =>
                                    handleChange(
                                        "exportNotifyEmail",
                                        e.target.value
                                    )
                                }
                            />
                        </div>

                        <div>
                            <label className="font-semibold text-sm">
                                Email Subject
                            </label>

                            <input
                                type="text"
                                placeholder="Enter export email subject"
                                className="w-full rounded py-2.5 px-4 mt-2 border-2 text-sm text-black outline-secondary"
                                value={settings.exportNotifySubject}
                                onChange={(e) =>
                                    handleChange(
                                        "exportNotifySubject",
                                        e.target.value
                                    )
                                }
                            />
                        </div>

                        {/* Ticket Notification */}
                        <div className="xm:col-span-2 mt-4">
                            <h3 className="text-lg font-bold border-b pb-2 mb-4">
                                Ticket Notification
                            </h3>
                        </div>

                        <div>
                            <label className="font-semibold text-sm">
                                Ticket Email
                            </label>

                            <input
                                type="email"
                                placeholder="Enter ticket notification email"
                                className="w-full rounded py-2.5 px-4 mt-2 border-2 text-sm text-black outline-secondary"
                                value={settings.ticketNotifyEmail}
                                onChange={(e) =>
                                    handleChange(
                                        "ticketNotifyEmail",
                                        e.target.value
                                    )
                                }
                            />
                        </div>

                        <div>
                            <label className="font-semibold text-sm">
                                Ticket Subject
                            </label>

                            <input
                                type="text"
                                placeholder="Enter ticket email subject"
                                className="w-full rounded py-2.5 px-4 mt-2 border-2 text-sm text-black outline-secondary"
                                value={settings.ticketNotifySubject}
                                onChange={(e) =>
                                    handleChange(
                                        "ticketNotifySubject",
                                        e.target.value
                                    )
                                }
                            />
                        </div>

                        {/* Buttons */}
                        <div className="xm:col-span-2 flex max-xm:flex-col gap-4 !mt-4">
                            <button
                                type="submit"
                                disabled={isProcessing}
                                className="px-6 py-2 min-w-[150px] rounded text-white text-sm font-semibold border-none outline-none bg-secondary hover:bg-custom-blue disabled:opacity-50"
                            >
                                {isProcessing ? (
                                    <>
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="18px"
                                            fill="#fff"
                                            className="mr-2 inline animate-spin"
                                            viewBox="0 0 26.349 26.35"
                                        >
                                            <circle
                                                cx="13.792"
                                                cy="3.082"
                                                r="3.082"
                                            />
                                            <circle
                                                cx="13.792"
                                                cy="24.501"
                                                r="1.849"
                                            />
                                            <circle
                                                cx="6.219"
                                                cy="6.218"
                                                r="2.774"
                                            />
                                            <circle
                                                cx="21.365"
                                                cy="21.363"
                                                r="1.541"
                                            />
                                        </svg>
                                        Processing...
                                    </>
                                ) : (
                                    "Save"
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AdminSettings;
